// This is a generic static SurveyJS configuration file
// any static configuration (custom widgets, properties, styles) should only be included here
// while generic dynamic configuration (scripts) should be put into useSurveyJSForm hook

import styled from '@emotion/styled';
import {css} from '@emotion/react';
import {CustomWidgetCollection, PanelModel, Question, Serializer, settings, surveyLocalization} from 'survey-core';
import {colors, fontFamily, fontSize, sizes} from 'patient-ping-remedy/packages/theme';
import {CustomDateWidget} from '@common/survey_js/CustomDateWidget';
import {CustomMultiSelectWidget} from '@common/survey_js/CustomMultiSelectWidget';
import 'survey-core/survey-core.min.css';
import 'survey-creator-core/survey-creator-core.min.css';

settings.storeUtcDates = true;

CustomWidgetCollection.Instance.addCustomWidget(CustomDateWidget, CustomDateWidget.name);
CustomWidgetCollection.Instance.addCustomWidget(CustomMultiSelectWidget, CustomMultiSelectWidget.name);
surveyLocalization.locales['en'].requiredError = '';

Serializer.addProperty("question", {
  name: "aiDescription:text",
  displayName: "Description for AI",
  category: "Care Journey Settings",
  visible: true
});

Serializer.addProperty("boolean", {
  name: "requiredBooleanValueForStepCompletion",
  choices: [
    { value: null, text: "(none)" },
    { value: true, text: "true" },
    { value: false, text: "false" }
  ],
  displayName: "Required value for step completion",
  category: "Care Journey Settings",
  visible: true
});

Serializer.addProperty("question", {
  name: "omitFromSchema",
  type:  "boolean",
  default: false,
  displayName: "Omit from schema",
  category: "Schema Generator",
  visible: true
});

const forceTypeInSchemaProperty = {
  name: "forceTypeInSchema",
  displayName: "Force type in Care Journey schema",
  category: "Schema Generator",
  type: 'dropdown',
  choices: [
    { value: null, text: 'Default' },
    { value: 'boolean', text: 'Boolean' },
    { value: 'integer', text: 'Integer' },
  ],
  visible: true
};

Serializer.addProperty('question', {
  name: 'customComponent',
  category: 'general',
  type: 'dropdown',
  choices: [
    { value: null, text: 'Default' },
    { value: CustomDateWidget.name, text: 'Remedy Date Picker' },
    { value: CustomMultiSelectWidget.name, text: 'Remedy Dropdown Multi-Group Select' },
  ],
  onSetValue(question: Question, value: string) {
    question.setPropertyValue('customComponent', value);
    question.updateCustomWidget();
  },
});

Serializer.addProperties('question', [
  {
    name: 'tooltip',
    category: 'layout',
    type: 'text',
    onSetValue(question: Question, value: string) {
      question.setPropertyValue('tooltip', value);
      if (value) {
        question.setPropertyValue('selector', 'h5');
      } else {
        question.resetPropertyValue('selector');
      }
    },
  },
  {
    name: 'selector',
    category: 'layout',
    type: 'string',
    visible: false,
  },
]);

Serializer.addProperty("checkbox", forceTypeInSchemaProperty);
Serializer.addProperty("tagbox", forceTypeInSchemaProperty);
Serializer.addProperty("radiogroup", forceTypeInSchemaProperty);
Serializer.addProperty("dropdown", forceTypeInSchemaProperty);
Serializer.addProperty("expression", forceTypeInSchemaProperty);

Serializer.addProperties('panel', [
  {
    name: 'tooltip',
    category: 'layout',
    type: 'text',
    onSetValue(model: PanelModel, value: string) {
      model.setPropertyValue('tooltip', value);
      if (value) {
        model.setPropertyValue('selector', 'h4');
      } else {
        model.resetPropertyValue('selector');
      }
    },
  },
  {
    name: 'selector',
    category: 'layout',
    type: 'string',
    visible: false,
  },
]);

Serializer.addProperty('question', {
  name: 'formsFieldsValidation',
  category: 'validation',
  type: 'buttongroup',
  default: true,
  choices: [
    { value: true, text: 'Enabled' },
    { value: false, text: 'Disabled' },
  ],
  onSetValue(question: Question, value: boolean) {
    question.setPropertyValue('formsFieldsValidation', value);
  },
});

const inputStyles = css`
  .sd-input.sd-text {
    --sjs-base-unit: ${sizes.xxSmall};
    --sjs-general-backcolor-dim-light: ${colors.white};
    --sjs-special-red-light: ${colors.white};

    padding-block: 0;
    height: ${sizes.large};
    border: 1px solid ${colors.gray2};

    &::placeholder {
      color: ${colors.gray4};
    }

    &.sd-input--readonly {
      background: ${colors.gray2};
    }

    &:not(.sd-input--readonly):focus {
      border: 1px solid ${colors.purple3};
    }

    &:not([type=text], [type=number]) {
      box-sizing: border-box;
      width: 100%;
    }

    &[type=number]::-webkit-inner-spin-button,
    &[type=number]::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &[type=number] {
      -moz-appearance: textfield;
    }
  }
`;

const textareaStyles = css`
  .sd-input.sd-comment {
    --sjs-general-backcolor-dim-light: ${colors.white};

    padding: ${sizes.xSmall};
    border: 1px solid ${colors.gray2};

    &.sd-input--readonly {
      background: ${colors.gray2};
    }

    &:not(.sd-input--readonly):focus {
      border: 1px solid ${colors.purple3};
    }
  }
`;

const dropdownStyles = css`
  .sv-dropdown_select-wrapper {
    .sd-dropdown {
      --sjs-base-unit: ${sizes.xxSmall};
      --sjs-general-backcolor-dim-light: ${colors.white};
      --sjs-special-red-light: ${colors.white};

      display: flex;
      align-items: center;
      padding-block: 0;
      height: ${sizes.large};
      border: 1px solid ${colors.gray2};

      input::placeholder {
        color: ${colors.gray4};
      }

      &.sd-input--readonly {
        background: ${colors.gray2};

        .sv-string-viewer {
          max-width: 90%;
        }
      }

      &:not(.sd-input--readonly):focus-within {
        border: 1px solid ${colors.purple3};
      }

      .sv-string-viewer {
        position: absolute;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }

      .sd-dropdown__hint-prefix,
      .sd-dropdown__hint-suffix {
        display: none;
      }
    }

    .sd-dropdown_chevron-button {
      --sjs-font-size: 2.6rem;
      --sjs-general-forecolor-light: ${colors.purple4};
    }

    .sd-dropdown_clean-button {
      --sjs-font-size: ${sizes.small};
      --sjs-general-forecolor-light: ${colors.purple4};
    }
  }
`;

const radioStyles = css`
  .sd-radio {
    --sjs-primary-backcolor: ${colors.green4};

    label {
      align-items: center;
      cursor: pointer;
    }

    input + span {
      height: 1.8rem;
      width: 1.8rem;
      background: ${colors.white};
      border: 1px solid ${colors.gray2};

      &::after {
        content: '';
        display: none;
        width: 1.2rem;
        height: 1.2rem;
        background: ${colors.green4};
        border-radius: 50%;
      }
    }

    input:focus + span {
      border-width: 0.2rem;
    }

    input:checked + span {
      border-color: ${colors.green4};

      &::after {
        display: block;
      }
    }

    &.sd-radio--readonly {
      --sjs-general-forecolor: ${colors.gray4};
      --sjs-primary-backcolor: ${colors.gray2};

      cursor: not-allowed;

      input + span {
        background: ${colors.gray2};

        &::after {
          background: ${colors.gray3};
        }
      }

      input:checked + span {
        border-color: ${colors.gray3};
      }
    }
  }
`;

const booleanStyles = css`
  .sd-boolean.sd-boolean--readonly {
    --sjs-general-forecolor: ${colors.gray4};
    --sjs-general-backcolor-dark: ${colors.gray2};

    cursor: not-allowed;
    pointer-events: all;

    & > * {
      pointer-events: none;
    }
  }
`;

const matrixStyles = css`
  .sd-question--table {
    .sd-question__content {
      --sjs-base-unit: 0.4rem;
      --sd-base-padding: ${sizes.small};

      .sd-table__question-wrapper {
        --sjs-base-unit: initial;
      }
    }

    .sd-matrixdynamic__remove-btn {
      --sjs-base-unit: 0.8rem;
    }

    .sd-matrixdynamic__add-btn {
      --sjs-base-unit: ${sizes.xSmall};

      margin-left: 0;
    }
  }
`;

export const SurveyJSTheme = styled.div`
  --sjs-general-forecolor: ${colors.charcoal};
  --sjs-general-backcolor: ${colors.white};
  --sjs-general-backcolor-dim: ${colors.white};
  --sjs-general-backcolor-dark: ${colors.purple1};

  --sjs-primary-forecolor: ${colors.white};
  --sjs-primary-backcolor: ${colors.purple4};
  --sjs-primary-backcolor-dark: ${colors.purple5};
  --sjs-primary-backcolor-light: ${colors.purple1};
  --sjs-primary-background-500: ${colors.purple4};

  --sjs-question-background: none;
  --sjs-special-background: ${colors.ash};
  --sjs-layer-3-background-500: ${colors.white};
  --sjs-secondary-background-500: ${colors.purple4};

  --sjs-font-family: ${fontFamily.default};
  --sjs-font-questiontitle-size: ${fontSize.small};

  --sjs-corner-radius: 8px;
  --sjs-editorpanel-cornerRadius: 0;

  --sjs-shadow-small: none;
  --sjs-shadow-inner: none;
  --sjs-shadow-inner-reset: none;

  .sv-popup {
    width: 100%;
    height: 100%;
  }

  .sd-page {
    --sd-base-padding: ${sizes.xSmall};
    --sd-base-vertical-padding: ${sizes.xxSmall};
    
    // gap between panels
    .sd-row--multiple {
      --sjs-base-unit: ${sizes.small};

      & > div {
        --sjs-base-unit: ${sizes.small};
        & > * {
          --sjs-base-unit: initial;
        }
      }

      .sd-row {
        --sjs-base-unit: ${sizes.small};
        & > * {
          --sjs-base-unit: initial;
        }
      }
    }

    // panel styles
    .sd-element--with-frame.sd-panel {
      --sd-base-padding: ${sizes.small};
      --sd-base-vertical-padding: ${sizes.small};
      border: 1px solid ${colors.gray3};

      .sd-panel__header {
        --sjs-font-questiontitle-size: ${fontSize.heading};
      }

      // gap between questions
      .sd-panel__content {
        --sd-base-padding: ${sizes.xSmall};

        & > .sd-row {
          --sd-base-vertical-padding: ${sizes.medium};
          & > * {
            --sd-base-vertical-padding: ${sizes.small};
          }
        }
      }
    }

    .sd-element__header {
      letter-spacing: 0.1rem;

      &::after {
        display: none;
      }

      .sd-element__title--readonly {
        color: ${colors.gray4};
      }
    }

    .sd-error {
      --sjs-special-red: ${colors.red5};
      --sjs-special-red-light: none;

      font-size: ${fontSize.small};
      font-weight: normal;
      padding: 0;
      margin-left: auto;
    }

    .sd-input--readonly {
      --sjs-general-forecolor: ${colors.gray4};
      --sjs-general-forecolor-light: ${colors.gray4};

      cursor: not-allowed;
    }

    ${inputStyles};
    ${textareaStyles};
    ${dropdownStyles};
    ${radioStyles};
    ${booleanStyles};
    ${matrixStyles};
  }
`;
